const MOVEMENT_MODES = ['walk', 'burrow', 'climb', 'fly', 'swim'];

const readDistance = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string') return null;
  // The first distance is the creature's own: a note or another form's speed after it is kept out, as in
  // "50 ft. (hover)", "30 ft. (40 ft. in Tiger form)" and the Werewolf's "30 ft., 40 ft. (wolf form only)".
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*(?:ft\.?|feet)?(?:\s*(?:\([^)]*\)|,.*))?$/i);
  if (!match) return null;
  const distance = Number(match[1]);
  return Number.isFinite(distance) && distance >= 0 ? distance : null;
};

/** Normalize every persisted RealmTracker movement shape without selecting a fastest mode. */
export const normalizeMovement = (movement) => {
  const normalized = { walk: null, burrow: null, climb: null, fly: null, swim: null, hover: null };
  if (typeof movement === 'number') {
    normalized.walk = readDistance(movement) ?? 0;
    return normalized;
  }

  if (Array.isArray(movement)) {
    movement.forEach((entry) => {
      const mode = String(entry?.type ?? entry?.mode ?? entry?.name ?? '').trim().toLowerCase();
      if (MOVEMENT_MODES.includes(mode)) normalized[mode] = readDistance(entry?.value ?? entry?.speed ?? entry?.distance);
      if (mode === 'hover') normalized.hover = true;
    });
    return normalized;
  }

  if (movement && typeof movement === 'object') {
    MOVEMENT_MODES.forEach((mode) => {
      const value = mode === 'walk' ? movement.walk ?? movement.walking : movement[mode];
      if (value !== undefined && value !== null && value !== '') normalized[mode] = readDistance(value);
    });
    if (typeof movement.hover === 'boolean') normalized.hover = movement.hover;
    // The SRD catalog marks hovering on the fly speed: { fly: '90 ft. (hover)' }.
    else if (typeof movement.fly === 'string' && /\(hover\)/i.test(movement.fly)) normalized.hover = true;
    return normalized;
  }

  if (typeof movement !== 'string') return normalized;
  const input = movement.trim();
  const labelled = new RegExp(`\\b(${MOVEMENT_MODES.join('|')}|walking)\\s*:?\\s*(-?\\d+(?:\\.\\d+)?)\\s*(?:ft\\.?|feet)`, 'ig');
  let match;
  let foundLabel = false;
  while ((match = labelled.exec(input)) !== null) {
    foundLabel = true;
    const mode = match[1].toLowerCase() === 'walking' ? 'walk' : match[1].toLowerCase();
    normalized[mode] = readDistance(`${match[2]} ft`);
  }
  if (/\bhover\b/i.test(input)) normalized.hover = true;
  // The SRD form begins with the unlabelled walking speed: "40 ft., fly 80 ft.".
  if (!/\b(?:walk|walking)\b/i.test(input)) {
    const leadingWalk = input.match(/^\s*(-?\d+(?:\.\d+)?)\s*(?:ft\.?|feet)(?:\s*[,;]|\s*$)/i);
    if (leadingWalk) normalized.walk = readDistance(`${leadingWalk[1]} ft`) ?? 0;
    else if (!foundLabel) normalized.walk = readDistance(input) ?? 0;
  }
  return normalized;
};

/**
 * Ordinary Speed is walking Speed. An absent creature/movement record is
 * unresolved; a resolved movement record without a walking mode has Speed 0.
 */
export const getWalkSpeed = (movement) => {
  if (movement === undefined || movement === null || movement === '') return null;
  return normalizeMovement(movement).walk ?? 0;
};

/** Apply generic set overrides after the additive, stacking-group-aware pipeline. */
export const getEffectiveWalkSpeed = (movement, effects, targetCombatantId) => {
  const walkSpeed = getWalkSpeed(movement);
  if (walkSpeed === null) return null;
  const active = (Array.isArray(effects) ? effects : effects?.activeEffects || [])
    .filter(effect => String(effect?.targetCombatantId) === String(targetCombatantId));
  // Revised Stunned/Incapacitated alone do not set Speed to zero.
  if (active.some(effect => ['condition:grappled','condition:restrained','condition:paralyzed','condition:petrified','condition:unconscious'].includes(effect.definitionId))) return 0;
  const exhaustion = Math.max(0, ...active.filter(effect => effect.definitionId === 'condition:exhaustion').map(effect => Number(effect.level ?? effect.exhaustionLevel) || 0));
  const modifiers = active
    .flatMap((effect) => effect?.modifiers || [])
    .filter((modifier) => modifier?.type === 'speed');
  const overrides = modifiers.filter((modifier) => modifier.operation === 'set' && Number.isFinite(Number(modifier.value)));
  const additiveModifiers = modifiers.filter((modifier) => modifier.operation === 'add');
  const grouped = new Map();
  let ungrouped = 0;
  additiveModifiers.forEach((modifier) => {
    const value = Number(modifier.value) || 0;
    if (!modifier.stackingGroup) ungrouped += value;
    else if (!grouped.has(modifier.stackingGroup)) grouped.set(modifier.stackingGroup, value);
    else grouped.set(modifier.stackingGroup, value < 0
      ? Math.min(grouped.get(modifier.stackingGroup), value)
      : Math.max(grouped.get(modifier.stackingGroup), value));
  });
  const additiveSpeed = Math.max(0, walkSpeed - 5 * exhaustion + ungrouped + [...grouped.values()].reduce((sum, value) => sum + value, 0));
  const factors = new Map();
  modifiers.filter(modifier => modifier.operation === 'multiply' && Number.isFinite(Number(modifier.value)) && Number(modifier.value) >= 0).forEach((modifier, i) => {
    const key = modifier.stackingGroup || `factor:${i}`;
    factors.set(key, Math.min(factors.get(key) ?? Infinity, Number(modifier.value)));
  });
  const scaledSpeed = Math.floor(additiveSpeed * [...factors.values()].reduce((product, value) => product * value, 1));
  if (overrides.some(modifier => Number(modifier.value) === 0)) return 0;
  return overrides.length ? Math.max(0, Number(overrides.at(-1).value)) : scaledSpeed;
};

